import { useEffect, useState, useCallback } from "react";
import React from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Sparkles, Play, Search, Crown, ChevronRight,
  Target, FileBarChart2, LayoutDashboard, Loader2, RefreshCw,
  TrendingUp, X, Trash2, Clock,
} from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, AreaChart, Area, FunnelChart, Funnel, LabelList, RadialBarChart, RadialBar, CartesianGrid, Legend } from "recharts";
import Sidebar from "../components/Sidebar";
import Logo from "../components/Logo";
import LangToggle from "../components/LangToggle";
import ThemeToggle from "../components/ThemeToggle";
import { useLang } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PURPLE = "#8B5CF6";
const GREEN = "#10B981";
const GREEN_LIGHT = "#A7F3D0";

const REFRESH_INTERVAL_MS = 30_000;

function formatHHMMSS(d) {
  if (!d) return "—";
  return d.toLocaleTimeString();
}

export default function Insights() {
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const [stats, setStats] = useState(null);
  const [extra, setExtra] = useState(null);  // funnel + timeline + by_source + heatmap + efficiency
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeSec, setActiveSec] = useState("dashboards");
  const [dashboards, setDashboards] = useState([]);
  const [goals, setGoals] = useState([]);
  const [reports, setReports] = useState([]);
  const [reportsLimit, setReportsLimit] = useState(250);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiReport, setAiReport] = useState(null);
  // Modals
  const [dashOpen, setDashOpen] = useState(false);
  const [dashName, setDashName] = useState("");
  const [dashDesc, setDashDesc] = useState("");
  const [dashSaving, setDashSaving] = useState(false);
  const [goalOpen, setGoalOpen] = useState(false);
  const [goalName, setGoalName] = useState("");
  const [goalMetric, setGoalMetric] = useState("saved_leads");
  const [goalTarget, setGoalTarget] = useState(50);
  const [goalSaving, setGoalSaving] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [s, d, g, r, ex] = await Promise.all([
        axios.get(`${API}/dashboard-stats`),
        axios.get(`${API}/dashboards`),
        axios.get(`${API}/goals`),
        axios.get(`${API}/reports`),
        axios.get(`${API}/insights/extra?days=30`).catch(() => ({ data: null })),
      ]);
      setStats(s.data);
      setDashboards(d.data.dashboards || []);
      setGoals(g.data.goals || []);
      setReports(r.data.reports || []);
      setReportsLimit(r.data.limit || 250);
      setExtra(ex.data || null);
      setLastUpdated(new Date());
    } catch (_e) { /* ignore */ }
  }, []);

  useEffect(() => {
    setLoading(true);
    loadAll().finally(() => setLoading(false));
  }, [loadAll]);

  // Auto-refresh every 30 seconds (silent — only the spinner pulses briefly)
  useEffect(() => {
    const id = window.setInterval(async () => {
      setRefreshing(true);
      await loadAll();
      setRefreshing(false);
    }, REFRESH_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [loadAll]);

  const manualRefresh = async () => {
    setRefreshing(true);
    await loadAll();
    setRefreshing(false);
    toast.success(t("ins.refreshed"));
  };

  const generateAi = async () => {
    setAiBusy(true);
    setAiReport(null);
    try {
      const { data } = await axios.post(`${API}/reports/ai-generate`, { lang });
      setAiReport(data.report);
      toast.success(t("ins.report_generated"));
      await loadAll();
    } catch (_e) {
      toast.error(t("ins.report_failed"));
    } finally { setAiBusy(false); }
  };

  const createDashboard = async () => {
    if (!dashName.trim()) { toast.error(t("ins.dash_name_required")); return; }
    setDashSaving(true);
    try {
      await axios.post(`${API}/dashboards`, {
        name: dashName.trim(),
        description: dashDesc.trim(),
      });
      toast.success(t("ins.dash_created", { name: dashName.trim() }));
      setDashOpen(false);
      setDashName(""); setDashDesc("");
      await loadAll();
    } catch (_e) {
      toast.error(t("ins.dash_failed"));
    } finally { setDashSaving(false); }
  };

  const deleteDashboard = async (id, name) => {
    if (!window.confirm(t("ins.dash_delete_confirm", { name }))) return;
    try {
      await axios.delete(`${API}/dashboards/${id}`);
      toast.success(t("ins.dash_deleted"));
      await loadAll();
    } catch (_e) { toast.error(t("ins.dash_failed")); }
  };

  const createGoal = async () => {
    if (!goalName.trim()) { toast.error(t("ins.goal_name_required")); return; }
    setGoalSaving(true);
    try {
      await axios.post(`${API}/goals`, {
        name: goalName.trim(),
        metric: goalMetric,
        target: Number(goalTarget) || 0,
        period: "month",
      });
      toast.success(t("ins.goal_created", { name: goalName.trim() }));
      setGoalOpen(false);
      setGoalName(""); setGoalTarget(50);
      await loadAll();
    } catch (_e) { toast.error(t("ins.goal_failed")); }
    finally { setGoalSaving(false); }
  };

  const deleteReport = async (id) => {
    try {
      await axios.delete(`${API}/reports/${id}`);
      toast.success(t("ins.report_deleted"));
      await loadAll();
    } catch (_e) { toast.error(t("ins.report_failed")); }
  };

  // Build chart series from real stats
  const sectorBars = (stats?.top_sectors || []).slice(0, 6).map((s) => ({
    name: s.sector?.length > 12 ? s.sector.slice(0, 12) + "…" : s.sector,
    count: s.count,
  }));
  const tempPie = stats ? [
    { name: "QUENTE", value: stats.by_badge?.QUENTE || 0, color: "#EF4444" },
    { name: "MORNO",  value: stats.by_badge?.MORNO  || 0, color: "#F59E0B" },
    { name: "FRIO",   value: stats.by_badge?.FRIO   || 0, color: GREEN },
  ] : [];
  const actionBars = stats ? Object.entries(stats.by_action || {}).slice(0, 5).map(([k, v]) => ({
    name: k.slice(0, 10),
    count: v,
  })) : [];

  const goalProgress = (g) => {
    if (!stats) return 0;
    const map = {
      saved_leads: stats.totals?.saved_leads ?? 0,
      quente: stats.by_badge?.QUENTE ?? 0,
      activity: stats.totals?.activity_count ?? 0,
      avg_score: stats.totals?.avg_score ?? 0,
    };
    const cur = map[g.metric] ?? 0;
    if (!g.target) return 0;
    return Math.min(100, Math.round((cur / g.target) * 100));
  };

  return (
    <div className="min-h-screen bg-[var(--al-bg)] text-[var(--al-text)]">
      <Sidebar />
      <div className="ml-[var(--al-sidebar-w,260px)]">
        <div data-testid="ins-trial-banner" className="bg-[#1A1A2E] text-white px-8 py-2 flex items-center justify-between text-[12px]">
          <div className="flex items-center gap-2">
            <Crown className="w-3.5 h-3.5 text-[#FBBF24]" />
            <span>{t("ins.greeting")}</span>
            <span className="opacity-60">·</span>
            <span>{t("ins.trial_left")}</span>
          </div>
          <a href="#billing" className="text-[#FBBF24] hover:underline">{t("ins.view_plans")}</a>
        </div>

        <header
          data-testid="ins-header"
          className="sticky top-0 z-30 bg-[var(--al-card-bg)] border-b border-[var(--al-border)] px-8 py-3 flex items-center gap-3"
        >
          <Logo className="w-8 h-8 object-contain" />
          <h1 className="text-[15px] font-semibold text-[var(--al-text)] mr-3">{t("ins.title")}</h1>

          <div className="flex-1 max-w-[420px] relative">
            <Search className="w-3.5 h-3.5 text-[var(--al-text-muted)] absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              data-testid="ins-search"
              type="text"
              placeholder={t("ins.search_placeholder")}
              className="al-input h-9 pl-8 text-[12.5px] w-full"
            />
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <button
              type="button"
              data-testid="ins-refresh"
              onClick={manualRefresh}
              title={t("ins.refresh_now")}
              className="w-9 h-9 rounded-lg border border-[var(--al-border)] hover:border-[#8B5CF6] hover:text-[#8B5CF6] inline-flex items-center justify-center"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            </button>
            <ThemeToggle />
            <LangToggle />
            <button
              type="button"
              data-testid="ins-avatar"
              title={t("ins.avatar_tooltip")}
              className="w-9 h-9 rounded-full bg-[#8B5CF6] text-white text-[12px] font-bold flex items-center justify-center hover:opacity-90"
            >
              RS
            </button>
          </div>
        </header>

        <div className="flex">
          <aside
            data-testid="ins-sub-sidebar"
            className="w-[260px] shrink-0 border-r border-[var(--al-border)] bg-[var(--al-card-bg)] py-6 px-4 hidden lg:block min-h-[calc(100vh-110px)]"
          >
            {/* Dashboards */}
            <SubSection
              icon={LayoutDashboard}
              titleKey="ins.section_dashboards"
              testid="dashboards"
              active={activeSec === "dashboards"}
              counter={String(dashboards.length)}
              onClick={() => setActiveSec("dashboards")}
            >
              {dashboards.length === 0 ? (
                <div className="text-[11px] italic text-[var(--al-text-muted)]">{t("ins.no_dashboards")}</div>
              ) : (
                <ul className="space-y-1">
                  {dashboards.slice(0, 5).map((d) => (
                    <li key={d.id} className="flex items-center justify-between gap-1.5 group">
                      <span className="text-[12.5px] text-[var(--al-text)] truncate">{d.name}</span>
                      <button
                        type="button"
                        data-testid={`ins-dash-del-${d.id}`}
                        onClick={() => deleteDashboard(d.id, d.name)}
                        className="opacity-0 group-hover:opacity-100 text-[var(--al-text-muted)] hover:text-[#DC2626]"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </SubSection>

            {/* Goals */}
            <SubSection
              icon={Target}
              titleKey="ins.section_goals"
              testid="goals"
              active={activeSec === "goals"}
              counter={String(goals.length)}
              onClick={() => setActiveSec("goals")}
            >
              {goals.length === 0 ? (
                <div className="text-[11px] italic text-[var(--al-text-muted)]">{t("ins.no_goals")}</div>
              ) : (
                <ul className="space-y-1.5">
                  {goals.slice(0, 5).map((g) => (
                    <li key={g.id}>
                      <div className="flex items-center justify-between text-[12px]">
                        <span className="truncate">{g.name}</span>
                        <span className="text-[var(--al-text-muted)] tabular-nums text-[10.5px]">{goalProgress(g)}%</span>
                      </div>
                      <div className="h-1 bg-[var(--al-border)] rounded mt-1 overflow-hidden">
                        <div className="h-1 bg-[#10B981]" style={{ width: `${goalProgress(g)}%` }} />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </SubSection>

            {/* Reports */}
            <SubSection
              icon={FileBarChart2}
              titleKey="ins.section_reports"
              testid="reports"
              active={activeSec === "reports"}
              counter={`${reports.length}/${reportsLimit}`}
              onClick={() => setActiveSec("reports")}
            >
              {reports.length === 0 ? (
                <div className="text-[11px] italic text-[var(--al-text-muted)]">{t("ins.no_reports")}</div>
              ) : (
                <ul className="space-y-1">
                  {reports.slice(0, 5).map((r) => (
                    <li key={r.id} className="flex items-center justify-between gap-1.5 group">
                      <span className="text-[12.5px] text-[var(--al-text)] truncate">{r.name}</span>
                      <button
                        type="button"
                        data-testid={`ins-rep-del-${r.id}`}
                        onClick={() => deleteReport(r.id)}
                        className="opacity-0 group-hover:opacity-100 text-[var(--al-text-muted)] hover:text-[#DC2626]"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </SubSection>
          </aside>

          <main className="flex-1 px-6 lg:px-10 py-8 max-w-[1300px] space-y-7">
            <section data-testid="ins-hero" className="text-center md:text-left">
              <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#8B5CF6] bg-[#F3E8FF] dark:bg-[#2A1B3D] px-2.5 py-1 rounded-full mb-3">
                <TrendingUp className="w-3 h-3" /> {t("ins.eyebrow")}
              </div>
              <h2 className="text-[28px] sm:text-[34px] font-extrabold text-[var(--al-text)] leading-tight tracking-tight max-w-[680px]">
                {t("ins.hero_title")}
              </h2>
              <p className="text-[13.5px] text-[var(--al-text-muted)] mt-3 leading-relaxed max-w-[640px]">
                {t("ins.hero_body")}
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-2.5 justify-center md:justify-start">
                <button
                  type="button"
                  data-testid="ins-create-dashboard"
                  onClick={() => setDashOpen(true)}
                  className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg text-[13px] font-semibold text-white"
                  style={{ background: PURPLE }}
                >
                  <Plus className="w-4 h-4" /> {t("ins.cta_create")}
                </button>
                <button
                  type="button"
                  data-testid="ins-create-goal"
                  onClick={() => setGoalOpen(true)}
                  className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg border border-[var(--al-border)] bg-[var(--al-card-bg)] text-[13px] font-semibold hover:border-[#10B981] hover:text-[#10B981]"
                >
                  <Target className="w-4 h-4" /> {t("ins.cta_goal")}
                </button>
                <button
                  type="button"
                  data-testid="ins-generate-ai"
                  onClick={generateAi}
                  disabled={aiBusy}
                  className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg border border-[#8B5CF6] bg-[#FAF5FF] text-[13px] font-semibold text-[#8B5CF6] hover:bg-[#F3E8FF] disabled:opacity-50"
                >
                  {aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {t("ins.cta_ai")}
                </button>
                <button
                  type="button"
                  data-testid="ins-watch-video"
                  className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg text-[13px] font-medium text-[var(--al-text-muted)] hover:text-[#3B82F6]"
                  onClick={() => toast.info(t("ins.video_demo"))}
                >
                  <Play className="w-4 h-4" /> {t("ins.cta_video")}
                </button>
              </div>
            </section>

            {/* AI report card (only after generation) */}
            {aiReport && (
              <section data-testid="ins-ai-report" className="al-card p-5 border-l-4 border-l-[#8B5CF6]">
                <div className="flex items-center justify-between mb-2">
                  <div className="inline-flex items-center gap-1.5 text-[12px] font-bold text-[#8B5CF6]">
                    <Sparkles className="w-3.5 h-3.5" /> {t("ins.ai_report_title")}
                  </div>
                  <button
                    type="button"
                    data-testid="ins-ai-report-close"
                    onClick={() => setAiReport(null)}
                    className="text-[var(--al-text-muted)] hover:text-[var(--al-text)]"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
                <p className="text-[13px] text-[var(--al-text)] leading-relaxed">{aiReport.report?.tldr}</p>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-[12px]">
                  <ReportColumn title={t("ins.ai_wins")} items={aiReport.report?.wins} color="#10B981" />
                  <ReportColumn title={t("ins.ai_risks")} items={aiReport.report?.risks} color="#EF4444" />
                  <ReportColumn title={t("ins.ai_actions")} items={aiReport.report?.next_actions} color="#3B82F6" />
                </div>
              </section>
            )}

            {/* Charts */}
            {loading ? (
              <div className="al-card p-8 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-[#8B5CF6]" />
              </div>
            ) : (
              <section data-testid="ins-charts" className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <ChartCard testid="ins-chart-sectors" title={t("ins.chart_sectors")} badge={t("ins.chart_realtime")}>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={sectorBars} margin={{ top: 6, right: 6, left: -20, bottom: 40 }}>
                      <XAxis dataKey="name" tick={{ fontSize: 10, fill: "var(--al-text-muted)" }} interval={0} angle={-35} textAnchor="end" height={60} />
                      <YAxis tick={{ fontSize: 10, fill: "var(--al-text-muted)" }} allowDecimals={false} />
                      <Tooltip cursor={{ fill: "rgba(139,92,246,.08)" }} contentStyle={{ background: "var(--al-card-bg)", border: "1px solid var(--al-border)", borderRadius: 8, fontSize: 11 }} />
                      <Bar dataKey="count" fill={PURPLE} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>

                <ChartCard testid="ins-chart-temperature" title={t("ins.chart_temperature")} ai>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie data={tempPie} dataKey="value" nameKey="name" innerRadius={42} outerRadius={70} paddingAngle={3} stroke="none">
                        {tempPie.map((s) => <Cell key={s.name} fill={s.color} />)}
                      </Pie>
                      <Tooltip contentStyle={{ background: "var(--al-card-bg)", border: "1px solid var(--al-border)", borderRadius: 8, fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex items-center justify-center gap-3 -mt-2 text-[11px]">
                    {tempPie.map((s) => (
                      <span key={s.name} className="inline-flex items-center gap-1">
                        <span className="w-2.5 h-2.5 rounded-sm" style={{ background: s.color }} />
                        {s.name}: <strong>{s.value}</strong>
                      </span>
                    ))}
                  </div>
                </ChartCard>

                <ChartCard testid="ins-chart-actions" title={t("ins.chart_actions")}>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={actionBars} margin={{ top: 6, right: 6, left: -20, bottom: 40 }}>
                      <XAxis dataKey="name" tick={{ fontSize: 10, fill: "var(--al-text-muted)" }} interval={0} angle={-35} textAnchor="end" height={60} />
                      <YAxis tick={{ fontSize: 10, fill: "var(--al-text-muted)" }} allowDecimals={false} />
                      <Tooltip cursor={{ fill: "rgba(16,185,129,.08)" }} contentStyle={{ background: "var(--al-card-bg)", border: "1px solid var(--al-border)", borderRadius: 8, fontSize: 11 }} />
                      <Bar dataKey="count" stackId="a" fill={GREEN} radius={[4, 4, 0, 0]} />
                      <Bar dataKey="count" stackId="a" fill={GREEN_LIGHT} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </section>
            )}

            {/* New analytical visualizations (Iter41) */}
            {extra && <ExtraVisualizations data={extra} stats={stats} goals={goals} t={t} />}

            {/* KPIs — each card drills down to the relevant screen */}
            {stats && (
              <section data-testid="ins-kpis" className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Kpi
                  label={t("ins.kpi_saved")}
                  value={stats.totals?.saved_leads ?? 0}
                  color={PURPLE}
                  testid="ins-kpi-saved"
                  drilldownHint={t("ins.kpi_saved_drill")}
                  onClick={() => navigate("/kanban")}
                />
                <Kpi
                  label={t("ins.kpi_avg_score")}
                  value={stats.totals?.avg_score ?? 0}
                  color={GREEN}
                  testid="ins-kpi-avg"
                  drilldownHint={t("ins.kpi_avg_drill")}
                  onClick={() => navigate("/kanban")}
                />
                <Kpi
                  label={t("ins.kpi_quente")}
                  value={stats.by_badge?.QUENTE ?? 0}
                  color="#EF4444"
                  testid="ins-kpi-quente"
                  drilldownHint={t("ins.kpi_quente_drill")}
                  onClick={() => navigate("/kanban?temp=QUENTE")}
                />
                <Kpi
                  label={t("ins.kpi_activity")}
                  value={stats.totals?.activity_count ?? 0}
                  color="#3B82F6"
                  testid="ins-kpi-activity"
                  drilldownHint={t("ins.kpi_activity_drill")}
                  onClick={() => navigate("/log")}
                />
              </section>
            )}
          </main>
        </div>
      </div>

      {/* Create Dashboard modal */}
      {dashOpen && (
        <Modal
          testid="ins-dash-modal"
          title={t("ins.dash_modal_title")}
          onClose={() => setDashOpen(false)}
        >
          <label className="block text-[11.5px] font-semibold text-[var(--al-text-muted)] uppercase tracking-wide mb-1">{t("ins.dash_field_name")}</label>
          <input
            type="text"
            data-testid="ins-dash-name"
            value={dashName}
            onChange={(e) => setDashName(e.target.value)}
            placeholder={t("ins.dash_placeholder_name")}
            className="al-input h-10 w-full text-[13px] mb-3"
            autoFocus
          />
          <label className="block text-[11.5px] font-semibold text-[var(--al-text-muted)] uppercase tracking-wide mb-1">{t("ins.dash_field_desc")}</label>
          <textarea
            data-testid="ins-dash-desc"
            value={dashDesc}
            onChange={(e) => setDashDesc(e.target.value)}
            placeholder={t("ins.dash_placeholder_desc")}
            rows={3}
            className="al-input w-full text-[13px]"
          />
          <div className="mt-4 flex items-center gap-2 justify-end">
            <button type="button" onClick={() => setDashOpen(false)} className="h-9 px-3 rounded-lg border border-[var(--al-border)] text-[12.5px]">
              {t("common.cancel")}
            </button>
            <button
              type="button"
              data-testid="ins-dash-save"
              onClick={createDashboard}
              disabled={dashSaving}
              className="h-9 px-4 rounded-lg text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5"
              style={{ background: PURPLE }}
            >
              {dashSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              {t("ins.dash_save")}
            </button>
          </div>
        </Modal>
      )}

      {/* Create Goal modal */}
      {goalOpen && (
        <Modal
          testid="ins-goal-modal"
          title={t("ins.goal_modal_title")}
          onClose={() => setGoalOpen(false)}
        >
          <label className="block text-[11.5px] font-semibold text-[var(--al-text-muted)] uppercase tracking-wide mb-1">{t("ins.goal_field_name")}</label>
          <input
            type="text"
            data-testid="ins-goal-name"
            value={goalName}
            onChange={(e) => setGoalName(e.target.value)}
            placeholder={t("ins.goal_placeholder_name")}
            className="al-input h-10 w-full text-[13px] mb-3"
            autoFocus
          />
          <label className="block text-[11.5px] font-semibold text-[var(--al-text-muted)] uppercase tracking-wide mb-1">{t("ins.goal_field_metric")}</label>
          <select
            data-testid="ins-goal-metric"
            value={goalMetric}
            onChange={(e) => setGoalMetric(e.target.value)}
            className="al-input h-10 w-full text-[13px] mb-3"
          >
            <option value="saved_leads">{t("ins.goal_m_saved")}</option>
            <option value="quente">{t("ins.goal_m_quente")}</option>
            <option value="activity">{t("ins.goal_m_activity")}</option>
            <option value="avg_score">{t("ins.goal_m_avg")}</option>
          </select>
          <label className="block text-[11.5px] font-semibold text-[var(--al-text-muted)] uppercase tracking-wide mb-1">{t("ins.goal_field_target")}</label>
          <input
            type="number"
            data-testid="ins-goal-target"
            value={goalTarget}
            onChange={(e) => setGoalTarget(e.target.value)}
            min={1}
            className="al-input h-10 w-full text-[13px]"
          />
          <div className="mt-4 flex items-center gap-2 justify-end">
            <button type="button" onClick={() => setGoalOpen(false)} className="h-9 px-3 rounded-lg border border-[var(--al-border)] text-[12.5px]">
              {t("common.cancel")}
            </button>
            <button
              type="button"
              data-testid="ins-goal-save"
              onClick={createGoal}
              disabled={goalSaving}
              className="h-9 px-4 rounded-lg text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5"
              style={{ background: "#10B981" }}
            >
              {goalSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              {t("ins.goal_save")}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function SubSection({ icon: Icon, titleKey, counter, testid, active, onClick, children }) {
  const { t } = useLang();
  return (
    <div className="mb-6">
      <button
        type="button"
        data-testid={`ins-sec-${testid}`}
        onClick={onClick}
        className={`w-full flex items-center justify-between text-left text-[10.5px] uppercase tracking-wider font-bold mb-2 ${active ? "text-[#8B5CF6]" : "text-[var(--al-text-muted)] hover:text-[var(--al-text)]"}`}
      >
        <span className="flex items-center gap-1.5">
          <Icon className="w-3 h-3" /> {t(titleKey)}
        </span>
        <span className="flex items-center gap-1.5">
          {counter && <span className="text-[10px] text-[var(--al-text-muted)] font-mono">{counter}</span>}
          <ChevronRight className={`w-3 h-3 transition-transform ${active ? "rotate-90" : ""}`} />
        </span>
      </button>
      <div className="pl-4 border-l border-[var(--al-border)]">
        {children}
      </div>
    </div>
  );
}

function ChartCard({ title, badge, ai, children, testid }) {
  return (
    <div className="al-card p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-[12.5px] font-bold text-[var(--al-text)]">{title}</h4>
        <div className="flex items-center gap-1.5">
          {ai && (
            <span className="inline-flex items-center gap-0.5 text-[9.5px] font-bold uppercase px-1.5 py-0.5 rounded bg-[#F3E8FF] text-[#8B5CF6]">
              <Sparkles className="w-2.5 h-2.5" /> AI
            </span>
          )}
          {badge && (
            <span className="text-[9.5px] font-semibold uppercase px-1.5 py-0.5 rounded bg-[#DCFCE7] text-[#16A34A]">
              {badge}
            </span>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}

function Kpi({ label, value, color, onClick, testid, drilldownHint }) {
  const interactive = !!onClick;
  const Component = interactive ? "button" : "div";
  return (
    <Component
      type={interactive ? "button" : undefined}
      onClick={onClick}
      data-testid={testid}
      className={`al-card p-4 text-left w-full ${interactive ? "hover:shadow-md hover:border-[color:var(--hover-color)] cursor-pointer transition-all group" : ""}`}
      style={interactive ? { "--hover-color": color } : undefined}
    >
      <div className="text-[10.5px] uppercase tracking-wider font-semibold text-[var(--al-text-muted)]">{label}</div>
      <div className="text-[26px] font-extrabold mt-1.5" style={{ color }}>{value}</div>
      {interactive && drilldownHint && (
        <div className="text-[10.5px] mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color }}>
          {drilldownHint} →
        </div>
      )}
    </Component>
  );
}

function ReportColumn({ title, items, color }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase font-bold tracking-wider mb-1.5" style={{ color }}>{title}</div>
      <ul className="space-y-1.5">
        {(items || []).map((it, i) => (
          <li key={`${title}-${i}-${String(it).slice(0, 40)}`} className="text-[var(--al-text)] leading-snug">• {it}</li>
        ))}
      </ul>
    </div>
  );
}

function Modal({ title, onClose, children, testid }) {
  return (
    <div
      data-testid={testid}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[460px] al-card p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[15px] font-bold text-[var(--al-text)]">{title}</h3>
          <button type="button" onClick={onClose} className="text-[var(--al-text-muted)] hover:text-[var(--al-text)]">
            <X className="w-4 h-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// =============================================================================
// ExtraVisualizations — 6 strategic charts wired to /api/insights/extra
// =============================================================================
const FUNNEL_COLORS = ["#8B5CF6", "#7C3AED", "#A78BFA", "#C084FC", "#10B981", "#EF4444"];
const SOURCE_COLORS = ["#8B5CF6", "#10B981", "#0EA5E9", "#F59E0B", "#EF4444", "#06B6D4", "#EC4899", "#84CC16"];

function ExtraVisualizations({ data, stats, goals, t }) {
  const funnel = (data.funnel || []).map((s, i) => ({ ...s, fill: FUNNEL_COLORS[i] }));
  const bySource = (data.by_source || []).slice(0, 8).map((r, i) => ({ ...r, fill: SOURCE_COLORS[i] }));
  const timeline = data.timeline || [];
  const efficiency = data.efficiency || [];
  const heatmap = data.heatmap || { cells: [], sectors: [], temperatures: ["QUENTE", "MORNO", "FRIO"], max: 1 };

  // Goal gauge — pick first active goal as the headline
  const headlineGoal = (goals || [])[0];
  const goalPct = (() => {
    if (!headlineGoal || !stats) return 0;
    const map = {
      saved_leads: stats.totals?.saved_leads ?? 0,
      avg_score: stats.totals?.avg_score ?? 0,
      activity_count: stats.totals?.activity_count ?? 0,
      quente: stats.by_badge?.QUENTE ?? 0,
    };
    const cur = map[headlineGoal.metric] ?? 0;
    const target = headlineGoal.target || 1;
    return Math.min(100, Math.round((cur / target) * 100));
  })();
  const gaugeData = [
    { name: "progress", value: goalPct, fill: goalPct >= 75 ? "#10B981" : goalPct >= 40 ? "#8B5CF6" : "#F59E0B" },
  ];

  return (
    <section data-testid="ins-extra-charts" className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-[15px] font-extrabold tracking-tight">{t("ins.extra_title")}</h3>
        <span className="text-[10.5px] uppercase tracking-wider text-[var(--al-text-muted)]">
          {t("ins.extra_subtitle")}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* 1. Funnel */}
        <ChartCard testid="ins-funnel" title={t("ins.funnel_title")}>
          <ResponsiveContainer width="100%" height={240}>
            <FunnelChart>
              <Tooltip contentStyle={{ background: "var(--al-card-bg)", border: "1px solid var(--al-border)", borderRadius: 8, fontSize: 11 }} />
              <Funnel dataKey="count" data={funnel} isAnimationActive>
                <LabelList position="right" fill="var(--al-text)" stroke="none" dataKey="stage" fontSize={11} />
                <LabelList position="center" fill="#fff" stroke="none" dataKey="count" fontSize={13} fontWeight={700} />
              </Funnel>
            </FunnelChart>
          </ResponsiveContainer>
          <div className="mt-2 grid grid-cols-3 gap-1.5 text-[10px] text-[var(--al-text-muted)]">
            {funnel.map((s) => (
              <div key={s.stage} className="flex items-center gap-1" data-testid={`ins-funnel-${s.stage}`}>
                <span className="w-2 h-2 rounded-sm" style={{ background: s.fill }} />
                <span className="font-bold">{s.stage}</span>
                <span className="ml-auto tabular-nums">{s.rate_from_prev}%</span>
              </div>
            ))}
          </div>
        </ChartCard>

        {/* 2. Lead source distribution */}
        <ChartCard testid="ins-source-donut" title={t("ins.source_title")}>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={bySource} dataKey="count" nameKey="source" innerRadius={50} outerRadius={85} paddingAngle={2} stroke="none">
                {bySource.map((s) => <Cell key={s.source} fill={s.fill} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "var(--al-card-bg)", border: "1px solid var(--al-border)", borderRadius: 8, fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-1 text-[10.5px]">
            {bySource.map((s) => (
              <div key={s.source} className="flex items-center gap-1.5" data-testid={`ins-source-${s.source}`}>
                <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: s.fill }} />
                <span className="truncate">{s.source}</span>
                <span className="ml-auto tabular-nums font-mono text-[var(--al-text-muted)]">{s.count}</span>
              </div>
            ))}
          </div>
        </ChartCard>

        {/* 3. Goal gauge */}
        <ChartCard testid="ins-goal-gauge" title={headlineGoal ? headlineGoal.name : t("ins.gauge_no_goal_title")}>
          {headlineGoal ? (
            <>
              <ResponsiveContainer width="100%" height={220}>
                <RadialBarChart innerRadius="65%" outerRadius="100%" data={gaugeData} startAngle={210} endAngle={-30}>
                  <RadialBar background dataKey="value" cornerRadius={8} />
                  <text x="50%" y="48%" textAnchor="middle" dominantBaseline="middle" className="fill-[var(--al-text)]" style={{ fontSize: 32, fontWeight: 800 }}>
                    {goalPct}%
                  </text>
                  <text x="50%" y="62%" textAnchor="middle" dominantBaseline="middle" className="fill-[var(--al-text-muted)]" style={{ fontSize: 11 }}>
                    {t("ins.gauge_of_target", { target: headlineGoal.target })}
                  </text>
                </RadialBarChart>
              </ResponsiveContainer>
              <div className="text-[11px] text-[var(--al-text-muted)] text-center">
                {t("ins.gauge_metric")}: <strong>{headlineGoal.metric}</strong>
              </div>
            </>
          ) : (
            <div className="h-[220px] flex flex-col items-center justify-center text-[12px] text-[var(--al-text-muted)] italic gap-2">
              <Target className="w-6 h-6 opacity-40" />
              {t("ins.gauge_no_goal")}
            </div>
          )}
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* 4. Timeline */}
        <ChartCard testid="ins-timeline" title={t("ins.timeline_title")} badge={t("ins.timeline_badge")}>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={timeline} margin={{ top: 6, right: 12, left: -16, bottom: 0 }}>
              <defs>
                <linearGradient id="leadsGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#8B5CF6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="actGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10B981" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="var(--al-border)" strokeDasharray="3 4" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 9, fill: "var(--al-text-muted)" }} tickFormatter={(d) => d.slice(5)} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 10, fill: "var(--al-text-muted)" }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "var(--al-card-bg)", border: "1px solid var(--al-border)", borderRadius: 8, fontSize: 11 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Area type="monotone" dataKey="leads" stroke="#8B5CF6" fill="url(#leadsGrad)" strokeWidth={2} name={t("ins.timeline_leads")} />
              <Area type="monotone" dataKey="activity" stroke="#10B981" fill="url(#actGrad)" strokeWidth={2} name={t("ins.timeline_activity")} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* 5. Action efficiency */}
        <ChartCard testid="ins-action-efficiency" title={t("ins.efficiency_title")}>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={efficiency} margin={{ top: 6, right: 12, left: -16, bottom: 0 }} layout="vertical">
              <CartesianGrid stroke="var(--al-border)" strokeDasharray="3 4" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10, fill: "var(--al-text-muted)" }} allowDecimals={false} />
              <YAxis type="category" dataKey="action" tick={{ fontSize: 10, fill: "var(--al-text-muted)" }} width={110} />
              <Tooltip contentStyle={{ background: "var(--al-card-bg)", border: "1px solid var(--al-border)", borderRadius: 8, fontSize: 11 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="events" fill="#A7F3D0" radius={[0, 4, 4, 0]} name={t("ins.efficiency_events")} />
              <Bar dataKey="qualified_plus" fill="#10B981" radius={[0, 4, 4, 0]} name={t("ins.efficiency_qualified")} />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-1 text-[10.5px] text-[var(--al-text-muted)]">
            {t("ins.efficiency_legend")}
          </div>
        </ChartCard>
      </div>

      {/* 6. Heatmap sector × temperature */}
      <ChartCard testid="ins-heatmap" title={t("ins.heatmap_title")}>
        {heatmap.sectors.length === 0 ? (
          <div className="text-[11.5px] italic text-[var(--al-text-muted)] py-6 text-center">{t("ins.heatmap_empty")}</div>
        ) : (
          <div className="overflow-x-auto">
            <div className="grid gap-1 min-w-[420px]" style={{ gridTemplateColumns: `minmax(120px,1.5fr) repeat(${heatmap.temperatures.length}, minmax(60px,1fr))` }}>
              <div></div>
              {heatmap.temperatures.map((tp) => (
                <div key={tp} className="text-[10px] font-bold uppercase text-center text-[var(--al-text-muted)] tracking-wider">{tp}</div>
              ))}
              {heatmap.sectors.map((sector) => (
                <React.Fragment key={sector}>
                  <div className="text-[11px] font-semibold truncate flex items-center">{sector}</div>
                  {heatmap.temperatures.map((tp) => {
                    const cell = heatmap.cells.find((c) => c.sector === sector && c.temperature === tp);
                    const n = cell?.count || 0;
                    const intensity = heatmap.max ? n / heatmap.max : 0;
                    const baseColor = tp === "QUENTE" ? "239,68,68" : tp === "MORNO" ? "245,158,11" : "16,185,129";
                    const bg = `rgba(${baseColor}, ${0.1 + intensity * 0.85})`;
                    return (
                      <div
                        key={`${sector}-${tp}`}
                        data-testid={`ins-heatmap-${sector}-${tp}`}
                        className="text-[12px] font-bold text-center py-2 rounded text-[var(--al-text)] transition-all hover:ring-2 hover:ring-[var(--al-text)]/20 cursor-default"
                        style={{ background: bg, color: intensity > 0.5 ? "#fff" : "var(--al-text)" }}
                        title={`${sector} · ${tp}: ${n}`}
                      >
                        {n || "—"}
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
            <div className="mt-3 text-[10.5px] text-[var(--al-text-muted)]">
              {t("ins.heatmap_legend")}
            </div>
          </div>
        )}
      </ChartCard>
    </section>
  );
}
